import { AxiosResponse } from 'axios';
import { call, fork, put, takeEvery } from 'redux-saga/effects';

import {
  DataModelTransition,
  DataModelTransitionParams,
} from '../domain/dataModelTransition';
import {
  buildModelTransition2ndRowNonTransitiveFields,
  buildModelTransitionRowListParams,
  buildModelTransitionTypeSeriesSijirituParams,
  resolveModelTransitionDefaultNonTransitiveFields,
  resolveModelTransitionFetchHalls,
  resolveModelTransitionMainField,
} from '../domain/modelTransition/mainField';
import { MAIN_FIELD_TYPE } from '../domain/schemas';
import { SettingsOptionsModelTransition } from '../domain/settingsOptionsModelTransition';
import { ShuOption } from '../domain/shu';

import {
  DataModelTransition2ndRowActionCreators,
  DataModelTransition2ndRowActionTypes,
  FetchDataModelTransition2ndRowAction,
  SearchDataModelTransition2ndRowAction,
  ToggleDataModelTransition2ndRowAction,
  singleDataModelTransition2ndRowSelector,
} from '../redux/server/dataModelTransition2ndRow';
import { settingsOptionsModelTransitionSelector } from '../redux/server/settingsOptionsModelTransition';
import {
  modelTransitionReportsSearchConditionSelector,
  modelTransitionReportsSelectedCurrentShuSelector,
  modelTransitionReportsSettingCurrentHallsSelector,
} from '../redux/ui/modelTransitionReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { selectShus2HallReportSearchCondition } from '../utils/shu';
import { handleErrorSaga } from './errorSaga';

/**
 * rowId を元に検索条件を加工する
 * @param rowId 行の識別子（機種コード / SISタイプコード / 機種タグID）
 * @param currentShu 選択中の種別の情報
 * @param searchParams 機種別推移の検索条件
 * @param searchCondition 機種別推移の設定項目
 * @returns 加工された検索条件
 */
export const queryParameterToFormConditions = (
  rowId: string,
  currentShu: ShuOption,
  searchParams: DataModelTransitionParams,
  searchCondition: SettingsOptionsModelTransition
): DataModelTransitionParams => {
  const resolvedMainField = resolveModelTransitionMainField(
    searchParams.mainField
  );
  // デフォルトの非推移項目
  const defaultNonTransition = resolveModelTransitionDefaultNonTransitiveFields(
    resolvedMainField,
    searchCondition.fields.nonTransition
  );
  // デフォルトの推移項目
  const defaultTransition = searchCondition.fields.transition
    .filter((value) => value.isDefault)
    .map((value) => value.code);

  // 表示項目が指定されていない時は、デフォルトの条件を指定する
  const fields = {
    transitiveFields:
      searchParams.transitiveFields === undefined
        ? defaultTransition
        : searchParams.transitiveFields,
    nonTransitiveFields: buildModelTransition2ndRowNonTransitiveFields(
      searchParams.nonTransitiveFields ?? defaultNonTransition,
      resolvedMainField
    ),
  };

  // MEMO: 現在選択中の種別を取得
  // テーブルの種別選択で変更した場合は元の検索条件に含まれていないため、改めて加工する
  const selectedShuParams = selectShus2HallReportSearchCondition([currentShu]);
  const isTypeSeries = resolvedMainField === MAIN_FIELD_TYPE.TYPE;
  const {
    kiList: _kiList,
    kiListForSijiritu: _kiListForSijiritu,
    ...searchParamsWithoutKiLists
  } = searchParams;

  return {
    ...(isTypeSeries ? searchParamsWithoutKiLists : searchParams),
    // MEMO: 展開行は出力区分に依らず hlMei を指定する（機種別と同じ）
    mainField: MAIN_FIELD_TYPE.HL_MEI,
    ...buildModelTransitionRowListParams(resolvedMainField, [rowId]),
    ...(isTypeSeries
      ? {
          ...buildModelTransitionTypeSeriesSijirituParams(searchParams),
          parentMainField: MAIN_FIELD_TYPE.TYPE,
        }
      : {
          // MEMO: ForSijiritu 系のパラメータは検索条件で指定されているものを分母として渡す
          kiListForSijiritu: searchParams.kiList,
        }),
    // MEMO: テーブルの種別選択を考慮した shuList / shuGroupIds を追加する
    // まずは元の検索条件から一旦 shuList / shuGroupIds を削除
    // 選択中の種別の情報を加える
    ...selectedShuParams,
    // MEMO: 表示項目が指定されていない時は、デフォルトの条件を指定する
    ...fields,
  };
};

/**
 * 機種別推移の展開行を表示する
 * @param action Action
 */
function* searchDataModelTransition2ndRowSaga(
  action: SearchDataModelTransition2ndRowAction
) {
  const { rowId } = action.payload;

  // APIリクエスト前に必要な情報を取得
  const searchParams = yield* customSelect(
    modelTransitionReportsSearchConditionSelector
  ); // 現在の検索条件

  // APIリクエスト前に必要な設定項目を取得
  const searchCondition = yield* customSelect(
    settingsOptionsModelTransitionSelector
  ); // 設定項目

  // APIリクエスト前に必要な選択中の種別を取得
  const currentShu = yield* customSelect(
    modelTransitionReportsSelectedCurrentShuSelector
  ); // 選択中の種別

  if (currentShu === undefined) {
    return;
  }

  const currentHalls = yield* customSelect(
    modelTransitionReportsSettingCurrentHallsSelector
  );

  // 検索条件を加工
  const params = queryParameterToFormConditions(
    rowId,
    currentShu,
    {
      ...searchParams,
      halls: resolveModelTransitionFetchHalls(currentHalls, searchParams.halls),
    },
    searchCondition
  );

  // 展開行を取得する
  yield put(
    DataModelTransition2ndRowActionCreators.fetchDataModelTransition2ndRowAction(
      rowId,
      params
    )
  );
}

/**
 * 機種別推移の展開行データを取得する
 * @param api AxiosInstance
 * @param action Action
 */
export function* fetchDataModelTransition2ndRowSaga(
  api: Api,
  action: FetchDataModelTransition2ndRowAction
) {
  const rowId = action.payload.rowId;
  const params = action.payload.params;

  try {
    yield put(
      DataModelTransition2ndRowActionCreators.fetchDataModelTransition2ndRowRequestAction(
        rowId,
        params
      )
    );

    const response: AxiosResponse<DataModelTransition> = yield call(
      api.get,
      '/data/kiTransition',
      buildConfig(params)
    );

    yield put(
      DataModelTransition2ndRowActionCreators.fetchDataModelTransition2ndRowSuccessAction(
        rowId,
        response.data
      )
    );
  } catch (error: unknown) {
    yield put(
      DataModelTransition2ndRowActionCreators.renewDataModelTransition2ndRowAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

/**
 * 指定したQueryParameterと一致する展開行を表示・非表示する
 * @param action Action
 */
function* toggleDataModelTransition2ndRowSaga(
  action: ToggleDataModelTransition2ndRowAction
) {
  const rowId = action.payload.rowId;

  if (!rowId) {
    throw new Error('rowId が指定されていません。');
  }

  // queryParameterをもとに展開行を取得
  const now2ndRow = yield* customSelect(
    singleDataModelTransition2ndRowSelector(rowId)
  );

  if (now2ndRow === undefined) {
    // 未取得の場合データを取得して表示する
    yield put(
      DataModelTransition2ndRowActionCreators.searchDataModelTransition2ndRowAction(
        rowId
      )
    );
  } else {
    // 取得済みの場合はデータを削除する
    yield put(
      DataModelTransition2ndRowActionCreators.hideDataModelTransition2ndRowAction(
        rowId
      )
    );
  }
}

/**
 * SEARCH_DATA_MODEL_TRANSITION_2NDROWがDispatchされた時の処理
 */
function* handleSearchDataModelTransition2ndRowSaga() {
  yield takeEvery(
    DataModelTransition2ndRowActionTypes.SEARCH_DATA_MODEL_TRANSITION_2NDROW,
    searchDataModelTransition2ndRowSaga
  );
}

/**
 * FETCH_DATA_MODEL_TRANSITION_2NDROWがDispatchされた時の処理
 */
function* handleFetchDataModelTransition2ndRowSaga(api: Api) {
  yield takeEvery(
    DataModelTransition2ndRowActionTypes.FETCH_DATA_MODEL_TRANSITION_2NDROW,
    fetchDataModelTransition2ndRowSaga,
    api
  );
}

/**
 * TOGGLE_DATA_MODEL_TRANSITION_2NDROWがDispatchされた時の処理
 */
function* handleToggleDataModelTransition2ndRowSaga() {
  yield takeEvery(
    DataModelTransition2ndRowActionTypes.TOGGLE_DATA_MODEL_TRANSITION_2NDROW,
    toggleDataModelTransition2ndRowSaga
  );
}

export function* dataModelTransition2ndRowSagas(context: { api: Api }) {
  yield fork(handleSearchDataModelTransition2ndRowSaga);
  yield fork(handleFetchDataModelTransition2ndRowSaga, context.api);
  yield fork(handleToggleDataModelTransition2ndRowSaga); // 展開行を表示・非表示
}
